import React from "react";
import { weekBounds } from "@/lib/semanas";
import { semaforoColor, ACTUAL_COLOR } from "@/lib/reporteCalc";

export const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
export const mx = (v) => (v == null ? "—" : `${Math.round(v) < 0 ? "-" : ""}$${Math.abs(Math.round(v)).toLocaleString("es-MX")}`);
export const kgFmt = (v) => (v == null ? "—" : Math.round(v).toLocaleString("es-MX"));
const dec = (v) => v.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sk = (w) => `${w.year}-${w.week}`;
export const th = "px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-center leading-tight text-white";
export const td = "px-3 py-1.5 text-right tabular-nums text-slate-900";
export const SEP = "border-l-[14px] border-white";
const MES_HEAD = "#003B73";
export const SEP_MES = "border-l-[28px] border-white";
// x ya es $/kg (o fracción de kilos para renglones en kg)
export const fmtKg = (r, x) => (x == null || !Number.isFinite(x) ? "—" : r.unit === "kg" ? `${dec(x * 100)}%` : `${x <= -0.005 ? "-" : ""}$${dec(Math.abs(x))}`);
const porKg = (r, v, kg) => (v == null || !kg ? "—" : fmtKg(r, v / kg));
export const valor = (r, v) => (r.unit === "kg" ? kgFmt(v) : mx(v));
export const rango = (w, ym) => {
  const [s, e] = weekBounds(w.year, w.week);
  const a = new Date(Math.max(s.getTime(), Date.UTC(ym.year, ym.month - 1, 1)));
  const b = new Date(Math.min(e.getTime(), Date.UTC(ym.year, ym.month, 0)));
  return `${a.getUTCDate()} al ${b.getUTCDate()} ${MESES[ym.month - 1]}`;
};

export const STICKY = "sticky left-0 z-10 max-w-[9.5rem] sm:max-w-none overflow-hidden text-ellipsis";

function Fila({ r, filas, ventaMes, corrida }) {
  const bg = r.total ? "bg-[var(--gf-total)]" : "";
  const label = <td className={`px-3 py-1.5 whitespace-nowrap ${STICKY} ${r.total ? "bg-[var(--gf-total)]" : "bg-white"}`}><span className="inline-block w-7 text-[10px] text-slate-500">{r.b || ""}</span>{r.label}</td>;
  if (r.pending) {
    return (
      <tr className={`border-t border-slate-100 font-bold ${bg}`} data-testid={`pm-row-${r.k}`}>
        {label}<td colSpan={filas.length * 2 + 3} className="px-3 py-1.5 text-center text-xs font-semibold text-amber-700" data-testid={`pm-${r.k}-pendiente`}>Pendiente: requiere el precio de compra por kilo (P.C.)</td>
      </tr>
    );
  }
  const semanas = filas.reduce((a, w) => a + (r.w(w) ?? 0), 0);
  const sep0 = corrida ? SEP_MES : SEP;
  return (
    <tr className={`border-t border-slate-100 font-bold ${bg}`} data-testid={`pm-row-${r.k}`}>
      {label}
      {!corrida && <td className={`${td} ${SEP_MES} font-normal`} data-testid={`pm-${r.k}-kg-mes`}>{porKg(r, r.mes, ventaMes)}</td>}
      {!corrida && <td className={td} data-testid={`pm-${r.k}-imp-mes`}>{valor(r, r.mes)}</td>}
      {filas.map((w, i) => [
        <td key={`${sk(w)}k`} className={`${td} ${i ? "border-l-4" : sep0} border-white font-normal`} data-testid={`pm-${r.k}-kg-${i + 1}`}>{porKg(r, r.w(w), w.kg)}</td>,
        <td key={`${sk(w)}i`} className={td} data-testid={`pm-${r.k}-imp-${i + 1}`}>{valor(r, r.w(w))}</td>])}
      {!corrida && <td className={`${td} ${SEP}`} data-testid={`pm-${r.k}-comp`}>{r.mes == null ? "—" : valor(r, r.mes - semanas)}</td>}
    </tr>
  );
}

// corrida = pestaña «Corrida semanal»: solo semanas reales, sin proyección mensual ni comprobación.
export function ProyeccionTabla({ filas: todas, ventaMes, rows, ym, corrida = false, onWeekClick, estados, actual, vacio = "Aún no hay semanas con venta real capturada en este mes.", testid }) {
  const filas = corrida ? todas.filter((w) => w.real) : todas;
  const kgSum = filas.reduce((a, w) => a + (w.kg ?? 0), 0);
  const cols = filas.length * 2 + (corrida ? 1 : 4);
  const sep0 = corrida ? SEP_MES : SEP;
  if (corrida && !filas.length) {
    return <p className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500" data-testid="cs-vacio">{vacio}</p>;
  }
  const sub = (cls = "") => [<td key="a" className={`text-center text-[10px] text-slate-500 ${cls}`}>$/kg</td>, <td key="b" className="text-center text-[10px] text-slate-500">Importe</td>];
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid={testid || (corrida ? "cs-tabla" : "pm-tabla")}>
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className={`${th} bg-[var(--gf-head)] text-left ${STICKY}`}>Fechas</th>
            {!corrida && <th colSpan={2} className={`${th} ${SEP_MES}`} style={{ backgroundColor: MES_HEAD }} data-testid="pm-head-mes">Proyección mensual<div className="font-normal normal-case">1 al {new Date(ym.year, ym.month, 0).getDate()} {MESES[ym.month - 1]} {ym.year}</div></th>}
            {filas.map((w, i) => (
              <th key={sk(w)} colSpan={2} className={`${th} bg-[var(--gf-head)] ${i ? "border-l-4" : sep0} border-white ${onWeekClick ? "cursor-pointer hover:brightness-110" : ""}`} style={actual === sk(w) ? { backgroundColor: ACTUAL_COLOR } : estados && semaforoColor(estados[sk(w)]) ? { backgroundColor: semaforoColor(estados[sk(w)]) } : w.real ? undefined : { backgroundColor: "#00AEEF" }}
                onClick={onWeekClick ? () => onWeekClick(w) : undefined} title={onWeekClick ? "Ver desglose de la semana" : undefined}
                data-testid={`pm-head-${i + 1}`} data-estado={w.real ? "real" : "proyeccion"} data-semaforo={estados ? (actual === sk(w) ? "actual" : String(estados[sk(w)] ?? "sin_dato")) : undefined}>
                <div className="text-[9px] font-semibold tracking-widest">{w.titulo || (w.real ? "REAL" : "PROYECCIÓN")}</div>{w.nombre || `Semana ${corrida ? w.week : i + 1}`}
                <div className="font-normal normal-case">{w.sub || rango(w, ym)}</div>
              </th>
            ))}
            {!corrida && <th className={`${th.replace("text-white", "")} bg-[var(--gf-comp)] text-slate-900 ${SEP}`}>Comprobación</th>}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-100 bg-[var(--gf-info)]">
            <td className={`px-3 py-1.5 text-xs font-semibold text-slate-600 ${STICKY} bg-[var(--gf-info)]`}>DÍAS</td>
            {!corrida && <td colSpan={2} className={`px-3 py-1.5 text-center font-bold ${SEP_MES}`} data-testid="pm-dias-mes">{filas.reduce((a, w) => a + w.dias, 0)}</td>}
            {filas.map((w, i) => <td key={sk(w)} colSpan={2} className={`px-3 py-1.5 text-center font-bold ${i ? "border-l-4" : sep0} border-white`} data-testid={`pm-dias-${i + 1}`}>{w.dias}</td>)}
            {!corrida && <td />}
          </tr>
          <tr className="border-t border-slate-100 font-bold" data-testid="pm-row-venta">
            <td className={`px-3 py-1.5 whitespace-nowrap ${STICKY} bg-white`}><span className="inline-block w-7" />VENTA EN KILOS</td>
            {!corrida && <td colSpan={2} className={`${td} ${SEP_MES}`} data-testid="pm-venta-mes">{kgFmt(ventaMes)}</td>}
            {filas.map((w, i) => <td key={sk(w)} colSpan={2} className={`${td} ${i ? "border-l-4" : sep0} border-white ${w.real ? "" : "italic text-slate-500"}`} data-testid={`pm-venta-${i + 1}`}>{kgFmt(w.kg)}</td>)}
            {!corrida && <td className={`${td} ${SEP}`} data-testid="pm-venta-comp">{ventaMes == null ? "—" : kgFmt(ventaMes - kgSum)}</td>}
          </tr>
          <tr><td className={`${STICKY} bg-white`} />{!corrida && sub(SEP_MES)}{filas.map((w, i) => <React.Fragment key={sk(w)}>{sub(`${i ? "border-l-4" : sep0} border-white`)}</React.Fragment>)}{!corrida && <td />}</tr>
          {rows.map((r, idx) => (r.gap
            ? <tr key={`gap${r.gap}`} aria-hidden><td colSpan={cols} style={{ height: 22, padding: 0 }} /></tr>
            : <React.Fragment key={r.k}>
                {r.total && <tr aria-hidden><td colSpan={cols} style={{ height: 10, padding: 0 }} /></tr>}
                <Fila r={r} filas={filas} ventaMes={ventaMes} corrida={corrida} />
              </React.Fragment>))}
        </tbody>
      </table>
    </div>
  );
}
